(ns common.schema.view.single-entity
  (:require
   [common.features.plan :as plan]
   [common.roles :as roles]
   [common.status :as statuses]))

(defn guest?
  [{:keys [role] :as _ctx}]
  (roles/is-guest-role? role))

(defn admin?
  [{:keys [role] :as _ctx}]
  (isa? roles/role-hierarchy role :role/admin))

(defn feature-access?
  [{:keys [tenant] :as _ctx} feature]
  (plan/available feature tenant))

(defn module-access?
  [{:keys [tenant account-current-permissions] :as ctx} module]
  (and (some #{module} (:tenant/modules tenant))
       (or (admin? ctx)
           (some #{module}
                 (->> account-current-permissions
                      :account-modules)))))

(defn section-config
  [{:keys [id title description icon-class] :as base-config}]
  (merge
   {:id                        id
    :title                     title
    :description               description
    :icon-class                icon-class
    :primary-entity-section?   true
    :required-features         nil
    :required-modules          nil
    :available-in-nev-2024?    true
    :always-visible?           false
    :can-add-and-remove-cards? false}
   base-config))

(defn summary-section
  [{:keys [id description] :as base-config}]
  (section-config
   (merge
    {:id                        id
     :title                     "Summary"
     :description               description
     :icon-class                "fal fa-list-alt"
     :always-visible?           true
     :can-add-and-remove-cards? true}
    base-config)))

(defn fees-section
  [{:keys [id description] :as base-config}]
  (section-config
   (merge
    {:id                id
     :title             "Fees"
     :description       description
     :icon-class        "fal fa-comments-dollar"
     :required-features [:feature/fee-tracking]
     :required-modules  [:module/fee-tracking]}
    base-config)))

(defn risks-section
  [{:keys [id description] :as base-config}]
  (section-config
   (merge
    {:id                        id
     :title                     "Risks"
     :description               description
     :icon-class                "fal fa-exclamation-triangle"
     :required-features         [:feature/risk-module]
     :required-modules          [:module/risk-module]
     :available-in-nev-2024?    true
     :can-add-and-remove-cards? false}
    base-config)))

(defn e-signatures-section
  [{:keys [id description] :as base-config}]
  (section-config
   (merge
    {:id                     id
     :title                  "E-signatures"
     :icon-class             "fal fa-pen"
     :description            description
     :required-features      [:feature/entity-esign]
     :available-in-nev-2024? false}
    base-config)))

(defn tasks-section
  [{:keys [id description] :as base-config}]
  (section-config
   (merge
    {:id          id
     :title       "Tasks"
     :icon-class  "fal fa-tasks"
     :description description}
    base-config)))

(defn link-group-section
  [{:keys [id description icon-class entity-types
           entity-filter-fn] :as base-config}]
  (section-config
   (merge
    {:id                      id
     :link-group?             true
     :primary-entity-section? false
     :linked-entity-section?  true
     :entity-types            entity-types
     :description             description
     :icon-class              icon-class
     :entity-filter-fn        entity-filter-fn}
    base-config)))

(defn counterparty-diligence-answers-section
  [{:keys [id]}]
  (section-config
   {:id id
    :title "Answers"
    :icon-class "fal fa-pencil-square"}))

;; TODO: This is still very work in progress
;; Permissions and feature access are not perfectly implemented yet
;; Also figure out how to handle the "always visible" sections, promos etc.
(def default-sections-by-entity-type
  {:entity.type/company
   [(summary-section
     {:id          :company-view/summary
      :description "General information about the company. You can freely add different cards to that section."})

    (tasks-section
     {:id          :company-view/tasks
      :description "Tasks associated with the company"})

    (fees-section
     {:id          :company-view/fees
      :description "Fees associated with the company"})

    (risks-section
     {:id          :company-view/risks
      :description "Risk directly and indirectly related to this company"})

    (link-group-section
     {:id               :company-view/deals-where-target-asset
      :title            "Target asset in deals"
      :description      "All deals where the company is a target asset"
      :icon-class       "fal fa-handshake"
      :entity-types     [:entity.type/oppo]
      :intra-nev?       true
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :target-asset-in-deal))
      :badge-label      (fn [{:keys [target-entity]}]
                          (-> target-entity
                              statuses/get-status
                              statuses/get-label))
      :badge-type       (fn [{:keys [target-entity]}]
                          (let [status (statuses/get-status target-entity)]
                            (cond
                              (statuses/active? status) :success
                              (statuses/on-hold? status) :warning
                              (statuses/passed? status) :danger
                              (statuses/portfolio? status) :info
                              :else nil)))
      :entity-filter-fn (fn [{:keys [primary-entity-id] :as _ctx} entity-candidate]
                          (= (:oppo/target-asset entity-candidate) primary-entity-id))})

    (link-group-section
     {:id               :company-view/investor-profiles-where-legal-entity
      :title            "Investor Profiles"
      :description      "Investor profiles where the company is the legal entity"
      :icon-class       "fal fa-user-tie"
      :entity-types     [:entity.type/investor]
      :entity-filter-fn (fn [{:keys [primary-entity-id] :as _ctx} entity-candidate]
                          (= (:investor/legal-entity entity-candidate) primary-entity-id))
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :investor-profile))})

    (link-group-section
     {:id               :company-view/holdings-where-target-asset
      :title            "Target asset in holdings"
      :description      "All holdings where the company is a target asset"
      :icon-class       "fal fa-briefcase"
      :entity-types     [:entity.type/holding]
      :entity-filter-fn (fn [{:keys [primary-entity-id] :as _ctx} entity-candidate]
                          (= (:holding/target-asset entity-candidate) primary-entity-id))
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :target-asset-in-holding))})

    (link-group-section
     {:id               :company-view/funds-where-gp
      :title            "Fund GP (legal entity) in funds"
      :description      "All funds where this company is a GP"
      :icon-class       "fal fa-seedling"
      :entity-types     [:entity.type/fof-asset]
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :gp-in-fund))
      :entity-filter-fn (fn [{:keys [primary-entity-id] :as _ctx} entity-candidate]
                          (= (:fof-asset/gp entity-candidate) primary-entity-id))})

    (link-group-section
     {:id               :company-view/agreements-where-customer
      :title            "Customer in agreements"
      :description      "Agreements where the company is a customer"
      :icon-class       "fal fa-file-signature"
      :entity-types     [:entity.type/sale-agreement]
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :party-in-agreement))
      ;; TODO investor-data-model: :sale-agreement/customer now stores only investor ids.
      ;; Matching company/person viewers through :investor/legal-entity must be decided
      ;; and implemented later instead of comparing directly to the legal-entity id.
      :entity-filter-fn (fn [{:keys [primary-entity-id] :as _ctx} entity-candidate]
                          (= (:sale-agreement/customer entity-candidate) primary-entity-id))})]

   :entity.type/person
   [(summary-section
     {:id          :person-view/summary
      :description "General information about the person"})

    (tasks-section
     {:id          :person-view/tasks
      :description "Tasks associated with the person"})

    (fees-section
     {:id          :person-view/fees
      :description "Fees associated with the person"})

    (risks-section
     {:id          :person-view/risks
      :description "Risks associated with the person"})

    (link-group-section
     {:id               :person-view/investor-profiles-where-legal-entity
      :title            "Investor Profiles"
      :description      "Investor profiles where the person is the legal entity"
      :icon-class       "fal fa-user-tie"
      :entity-types     [:entity.type/investor]
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :investor-profile))
      :entity-filter-fn (fn [{:keys [primary-entity-id]} entity-candidate]
                          (= (:investor/legal-entity entity-candidate) primary-entity-id))})

    (link-group-section
     {:id               :person-view/agreements-where-customer
      :title            "Agreements"
      :description      "Agreements where the person is customer"
      :icon-class       "fal fa-file-signature"
      :entity-types     [:entity.type/sale-agreement]
      :kicker-text      (fn [{:keys [tr]}]
                          (tr :party-in-agreement))
      ;; TODO investor-data-model: :sale-agreement/customer now stores only investor ids.
      ;; Matching company/person viewers through :investor/legal-entity must be decided
      ;; and implemented later instead of comparing directly to the legal-entity id.
      :entity-filter-fn (fn [{:keys [primary-entity-id] :as _ctx} entity-candidate]
                          (= (:sale-agreement/customer entity-candidate) primary-entity-id))})]

   ;; TODO: Missing kicker text and other stuff,
   ;; implement later when oppo nev is implemented
   :entity.type/oppo
   [(summary-section
     {:id          :oppo-view/summary
      :title       "Deal summary"
      :description "General information about the deal"})

    (tasks-section
     {:id          :oppo-view/tasks
      :title       "Deal Tasks"
      :description "Tasks associated with the deal"})

    (risks-section
     {:id          :oppo-view/risks
      :title       "Deal Risks"
      :description "Risks associated with the deal"})

    (e-signatures-section
     {:id          :oppo-view/e-signatures
      :title       "Deal E-signatures"
      :description "E signatures for the deal"})

    (section-config
     {:id                :oppo-view/decisions
      :title             "Decisions"
      :description       "Decisions associated with the deal"
      :icon-class        "fal fa-hand-holding-seedling"
      :required-features [:feature/dealflow :feature/decisions]
      :required-modules  [:module/dealflow :module/decisions]})

    (section-config
     {:id                     :oppo-view/pre-investment-checks
      :title                  "Pre investment checks"
      :icon-class             "fal fa-user-check"
      :required-features      [:feature/pre-investment-checks]
      :available-in-nev-2024? false})

    (link-group-section
     {:id               :oppo-view/target-assets
      :title            "Target asset(s)"
      :description      "All target assets for the deal"
      :entity-types     [:entity.type/company
                         :entity.type/fof-asset
                         :entity.type/real-estate]
      :entity-filter-fn (fn [{:keys [primary-entity]} entity-candidate]
                          (= (:oppo/target-asset primary-entity) (:db/id entity-candidate)))})]

   :entity.type/investor
   [(summary-section
     {:id          :investor-view/summary
      :title       "Investor profile summary"
      :description "General information about the investor profile"})]

   :entity.type/holding
   [(summary-section
     {:id          :holding-view/summary
      :title       "Holding summary"
      :description "General information about the holding"})
    (risks-section
     {:id          :holding-view/risks
      :title       "Holding risks"
      :description "Holding risks"})]

   :entity.type/sale-agreement
   [(summary-section
     {:id    :sale-agreement-view/summary
      :title "Agreement summary"})]

   :entity.type/fof-asset
   [(summary-section
     {:id    :fof-asset-view/summary
      :title "Fund summary"})]

   :entity.type/survey-profile
   [(summary-section
     {:id    :survey-profile-view/summary
      :title "Summary"})]

   :entity.type/project-counterparty-diligence
   [(summary-section
     {:id    :project-counterparty-diligence-view/summary
      :title "Summary"})
    (counterparty-diligence-answers-section
     {:id :project-counterparty-diligence-view/answers})]

   :entity.type/company-counterparty-diligence
   [(summary-section
     {:id    :company-counterparty-diligence-view/summary
      :title "Summary"})
    (counterparty-diligence-answers-section
     {:id :company-counterparty-diligence-view/answers})]

   :entity.type/person-counterparty-diligence
   [(summary-section
     {:id    :person-counterparty-diligence-view/summary
      :title "Summary"})
    (counterparty-diligence-answers-section
     {:id :person-counterparty-diligence-view/answers})]})

(defn default-sections
  [entity-type]
  (get default-sections-by-entity-type entity-type))

(def entity-type-to-section-id-to-section-config
  (->> default-sections-by-entity-type
       (mapv (fn [[entity-type sections]]
               [entity-type (->> sections
                                 (mapv (fn [{:keys [id] :as section}]
                                         [id section]))
                                 (into {}))]))
       (into {})))

(def summary-section-ids-by-entity-type
  {:entity.type/company                        :company-view/summary
   :entity.type/oppo                           :oppo-view/summary
   :entity.type/person                         :person-view/summary
   :entity.type/investor                       :investor-view/summary
   :entity.type/sale-agreement                 :sale-agreement-view/summary
   :entity.type/holding                        :holding-view/summary
   :entity.type/fof-asset                      :fof-asset-view/summary
   :entity.type/project-counterparty-diligence :project-counterparty-diligence-view/summary
   :entity.type/company-counterparty-diligence :company-counterparty-diligence-view/summary
   :entity.type/person-counterparty-diligence  :person-counterparty-diligence-view/summary
   :entity.type/survey-profile                 :survey-profile-view/summary})

(defn summary-section-id
  [entity-type]
  (get summary-section-ids-by-entity-type entity-type))

(defn fees-section-id
  [entity-type]
  (case entity-type
    :entity.type/company :company-view/fees
    :entity.type/person :person-view/fees))

(defn tasks-section-id
  [entity-type]
  (case entity-type
    :entity.type/company :company-view/tasks
    :entity.type/person :person-view/tasks))

(def sections-by-id
  (->> default-sections-by-entity-type
       (mapcat (fn [[_et sections]]
                 (->> sections
                      (mapv (fn [{:keys [id] :as section}]
                              [id section])))))
       (into {})))

(defn available-in-nev-2024?
  [section-id]
  (->> section-id
       (get sections-by-id)
       :available-in-nev-2024?))

(defn primary-entity-section?
  [section-id]
  (->> section-id
       (get sections-by-id)
       :primary-entity-section?))

(defn linked-entity-section?
  [section-id]
  (->> section-id
       (get sections-by-id)
       :linked-entity-section?))

(defn section-system-visibility
  [ctx {:keys [required-features always-visible?] :as _section}]
  (let [result (cond
                 always-visible? :visible

                 (not required-features) :visible

                 (->> required-features
                      (every? #(feature-access? ctx %))) :visible

                 :else :hidden)]
    result))

(defn section-access
  [ctx {:keys [always-visible? required-modules] :as section}]
  (let [system-visibility (section-system-visibility ctx section)
        result (cond
                 (= system-visibility :hidden)
                 :no-access

                 (or always-visible?
                     (not required-modules)
                     (->> required-modules
                          (every? #(module-access? ctx %))))
                 :full-access

                 :else :no-module-access)]
    result))

(defn section-helper-text
  [{:keys [tr] :as ctx} {:keys [available-in-nev-2024?] :as section}]
  (case (section-access ctx section)
    :no-module-access
    (tr :view-section/no-module-access-helper-text)

    :full-access
    (when (not available-in-nev-2024?)
      (tr :view-section/not-available-in-nev-2024-helper-text))

    nil))

(defn available-sections
  [ctx entity-type]
  (->> (get default-sections-by-entity-type entity-type)
       (filterv #(= :visible (section-system-visibility ctx %)))))

(defn passes-section-entity-filter?
  [{:keys [section-id] :as ctx} entity-candidate]
  (let [entity-filter-fn (->> section-id
                              (get sections-by-id)
                              :entity-filter-fn)]
    ;; TODO: Throw error?
    (if-not (fn? entity-filter-fn)
      false
      (entity-filter-fn (dissoc ctx :section-id) entity-candidate))))

(defn kicker-text
  [ctx {:keys [kicker-text] :as _section}]
  (cond (fn? kicker-text)
        (kicker-text ctx)
        (string? kicker-text)
        kicker-text
        :else nil))

(defn badge-label
  [ctx {:keys [badge-label] :as _section}]
  (cond (fn? badge-label)
        (badge-label ctx)
        (string? badge-label)
        badge-label
        :else nil))

(defn badge-type
  [ctx {:keys [badge-type] :as _section}]
  (cond (fn? badge-type)
        (badge-type ctx)
        (keyword? badge-type)
        badge-type
        :else nil))
